<i18n
  src="@/locales/components/dashboard/agro_exporter/certifications_expired.yml"
  lang="yaml"
></i18n>
<template>
  <div class="bg-white shadow rounded-lg p-4 sm:p-6 xl:p-8">
    <div class="mb-4 flex justify-between">
      <div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">
          {{ $t("labels.title") }}
        </h3>
        <span class="text-base font-normal text-gray-500">
          {{ $t("labels.description") }}
        </span>
      </div>
      <div class="flex-shrink-0">
        <router-link
          class="text-sm font-medium text-lime-600 hover:text-lime-700 cursor-pointer hover:bg-gray-100 rounded-lg p-2"
          :to="{
            name: routeName(routePermission, 'ae_certifications'),
            params: {
              agro_exporter_id: agro_exporter_id
            },
          }"
        >
          {{ $t("labels.show_link") }}
        </router-link>
      </div>
    </div>
    <template v-if="load_general_data">
      <div class="flex flex-col mt-8">
        <div id="chart">
          <apexchart
            type="donut"
            :options="chartOptions"
            :series="series"
            :height="175"
          ></apexchart>
        </div>
      </div>
      <div class="flex flex-col mt-8">
        <div class="overflow-x-auto rounded-lg">
          <div class="align-middle inline-block min-w-full">
            <div class="shadow overflow-hidden sm:rounded-lg">
              <table class="table">
                <thead class="table-header">
                  <tr>
                    <th
                      scope="col"
                      class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    >
                      {{ $t("labels.provider") }}
                    </th>
                    <th
                      scope="col"
                      class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    >
                      {{ $t("labels.certification") }}
                    </th>
                    <th
                      scope="col"
                      class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    >
                      {{ $t("labels.expiration_date") }}
                    </th>
                    <th
                      scope="col"
                      class="px-2 py-4 text-left text-base font-medium text-gray-700"
                    >
                      {{ $t("labels.status") }}
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white table-body">
                  <tr
                    v-for="(
                      certification, index
                    ) in certifications.certifications"
                    :key="index"
                    class="table-row"
                  >
                    <td
                      class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                    >
                      {{ certification.provider }}
                    </td>
                    <td
                      class="p-3 whitespace-nowrap text-sm font-normal text-gray-900"
                    >
                      {{ certification.certification }}
                    </td>
                    <td
                      class="p-3 whitespace-nowrap text-sm font-normal text-gray-500"
                    >
                      <span
                        :class="[
                          'font-semibold',
                          guiExpirationDate(certification.expiration_date),
                        ]"
                      >
                        {{ dateFormatter(certification.expiration_date) }}
                      </span>
                    </td>
                    <td
                      class="p-3 whitespace-nowrap text-sm font-normal text-gray-500"
                    >
                      <span
                        :class="[
                          'font-semibold',
                          guiExpirationDate(certification.expiration_date),
                        ]"
                      >
                        {{ status(certification.expiration_date) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import Requester from "@/components/views/dashboard/requesters/agro_exporter/certifications";
export default {
  name: "DashboardAECertificationsExpired",
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      certifications: {},
      certifications_stats: null,
      load_general_data: false,
      chartOptions: {
        chart: { type: "donut" },
        labels: [],
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + "%";
          },
          textAnchor: "middle",
          distributed: false,
          offsetX: 0,
          offsetY: 0,
          style: {
            fontSize: "16px",
            fontFamily: "Helvetica, Arial, sans-serif",
            fontWeight: "bold",
            colors: ["#fff"],
          },
          background: {
            enabled: false,
            foreColor: "#fff",
            padding: 4,
            borderRadius: 2,
            borderWidth: 1,
            borderColor: "#fff",
            opacity: 0.9,
            dropShadow: {
              enabled: false,
            },
          },
          dropShadow: {
            enabled: false,
          },
        },
        colors: ["#CC3333", "#FF6600", "#65A30D"],
        legend: { offsetX: 100, fontSize: "16px" },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: { height: 200 },
              legend: {
                position: "bottom",
                offsetX: 0,
              },
            },
          },
        ],
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: "50%",
            },
          },
        },
        tooltip: {
          enabled: false,
        },
      },
    };
  },
  props: {
    agro_exporter_id: { required: true },
  },
  computed: {
    series() {
      return [
        Number(this.certifications_stats.percentage_expired || 0),
        Number(this.certifications_stats.percentage_to_expire || 0),
        Number(this.certifications_stats.percentage_valid || 0),
      ];
    },
    routePermission() {
      if (
        this.checkIfUserIsAuthorized(
          "certifications",
          "providers",
          "agro_exporter"
        )
      )
        return "providers";
      else if (
        this.checkIfUserIsAuthorized(
          "certifications",
          "query_providers",
          "agro_exporter"
        )
      )
        return "query_providers";
      else return "";
    },
  },
  methods: {
    async fetchFirstTenActiveCertifications() {
      const response = await Requester.fetchFirstTenActiveCertifications(
        this.agro_exporter_id
      );
      this.certifications = response.data;
      
    },
    async fetchCertificationsStats() {
      const response = await Requester.fetchCertificationsStats(
        this.agro_exporter_id
      );
      this.certifications_stats = response.data;
      
      this.chartOptions.labels = [
        this.$t("labels.percentage_expired") +
          ": " +
          this.certifications_stats.total_expired,
        this.$t("labels.percentage_to_expire") +
          ": " +
          this.certifications_stats.total_to_expire,
        this.$t("labels.percentage_valid") +
          ": " +
          this.certifications_stats.total_valid,
      ];
      this.load_general_data = true;
    },
    status(expiration_date) {
      const diff_days = this.daysUntilExpirationDate(expiration_date);
      return diff_days >= 0
        ? `${diff_days} ${this.$t("labels.days")}`
        : this.$t("labels.expired");
    },
    guiExpirationDate(expiration_date) {
      const diff_days = this.daysUntilExpirationDate(expiration_date);

      if (diff_days < 0) {
        return "text-red-500";
      }
    },
  },
  created() {
    this.fetchFirstTenActiveCertifications();
    this.fetchCertificationsStats();
  },
};
</script>
